import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../api/apiConfig';
import { format, parseISO } from 'date-fns';
import { NewspaperIcon, EnvelopeOpenIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface EmailMessage {
  id: number;
  subject: string;
  sender: string;
  sender_name?: string;
  body: string;
  received_at: string;
  is_newsletter: boolean;
  person_detail?: { id: number; name: string } | null;
  summary?: string;
}

const NewsletterList: React.FC = () => {
  const [newsletters, setNewsletters] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedSender, setSelectedSender] = useState<string>('all');
  const [summarizing, setSummarizing] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const fetchNewsletters = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/emails/');
      // Filter for newsletters only
      const newsletterEmails = response.data.filter((email: EmailMessage) => email.is_newsletter);
      setNewsletters(newsletterEmails);
    } catch (err: any) {
      console.error('Error fetching newsletters:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch newsletters');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const stripHtmlTags = (html: string) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Get unique senders for filter
  const senders = useMemo(() => {
    const uniqueSenders = new Map<string, string>();
    newsletters.forEach(email => {
      const displayName = email.person_detail?.name || email.sender_name || email.sender;
      uniqueSenders.set(email.sender, displayName);
    });
    return Array.from(uniqueSenders.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [newsletters]);

  // Filter newsletters by sender
  const filteredNewsletters = useMemo(() => {
    if (selectedSender === 'all') return newsletters;
    return newsletters.filter(email => email.sender === selectedSender);
  }, [newsletters, selectedSender]);

  const handleGenerateSummary = async (newsletterId: number) => {
    setSummarizing(prev => ({ ...prev, [newsletterId]: true }));
    try {
      const response = await apiClient.post(`/api/emails/${newsletterId}/generate_summary/`, {
        model: 'gpt-4o-mini'
      });

      // Update newsletter with summary
      setNewsletters(prevNewsletters => prevNewsletters.map(email => {
        if (email.id === newsletterId) {
          return {
            ...email,
            summary: response.data.summary
          };
        }
        return email;
      }));

    } catch (error: any) {
      console.error('Error generating summary:', error);
      alert(error.response?.data?.error || 'Failed to generate summary');
    } finally {
      setSummarizing(prev => ({ ...prev, [newsletterId]: false }));
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '256px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          border: `2px solid ${COLORS.border.default}`,
          borderBottomColor: COLORS.accent.default,
          animation: 'spin 1s linear infinite'
        }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#FEF2F2',
        border: `1px solid ${COLORS.status.error}`,
        borderRadius: RADIUS,
        padding: SPACING[16]
      }}>
        <p style={{ color: COLORS.status.error, margin: 0 }}>Error: {error}</p>
      </div>
    );
  }

  if (newsletters.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: SPACING[48],
        backgroundColor: COLORS.bg.base,
        borderRadius: RADIUS,
        border: `2px dashed ${COLORS.border.default}`
      }}>
        <NewspaperIcon style={{
          margin: '0 auto',
          height: '48px',
          width: '48px',
          color: COLORS.text.tertiary
        }} />
        <h3 style={{
          marginTop: SPACING[8],
          fontSize: TYPOGRAPHY.size.sm,
          fontWeight: TYPOGRAPHY.weight.medium,
          color: COLORS.text.primary
        }}>No newsletters</h3>
        <p style={{
          marginTop: SPACING[4],
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.text.secondary
        }}>
          Mark email senders as newsletters from the Email List to see them here.
        </p>
        <div style={{ marginTop: SPACING[24] }}>
          <Link
            to="/emails"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: `${SPACING[8]} ${SPACING[16]}`,
              border: 'none',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.medium,
              borderRadius: RADIUS,
              color: COLORS.bg.panel,
              backgroundColor: COLORS.accent.default,
              textDecoration: 'none'
            }}
          >
            Go to Emails
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: SPACING[16]
      }}>
        <p style={{
          fontSize: TYPOGRAPHY.size.sm,
          color: COLORS.text.secondary,
          margin: 0
        }}>
          {filteredNewsletters.length} newsletter{filteredNewsletters.length !== 1 ? 's' : ''}
          {selectedSender !== 'all' && ` from ${senders.find(s => s[0] === selectedSender)?.[1]}`}
        </p>

        {/* Filter by sender */}
        {senders.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <label htmlFor="sender-filter" style={{
              fontSize: TYPOGRAPHY.size.sm,
              color: COLORS.text.secondary
            }}>Filter by sender:</label>
            <select
              id="sender-filter"
              value={selectedSender}
              onChange={(e) => setSelectedSender(e.target.value)}
              style={{
                padding: `${SPACING[4]} ${SPACING[12]}`,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                fontSize: TYPOGRAPHY.size.sm,
                outline: 'none',
                backgroundColor: COLORS.bg.panel
              }}
            >
              <option value="all">All Senders</option>
              {senders.map(([email, name]) => (
                <option key={email} value={email}>{name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
        {filteredNewsletters.map((newsletter) => (
          <div
            key={newsletter.id}
            style={{
              backgroundColor: COLORS.bg.panel,
              border: `1px solid ${COLORS.border.default}`,
              borderRadius: RADIUS,
              overflow: 'hidden',
              transition: 'box-shadow 0.2s ease'
            }}
          >
            <div
              style={{ padding: SPACING[16], cursor: 'pointer' }}
              onClick={() => toggleExpanded(newsletter.id)}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: SPACING[12],
                  flex: 1
                }}>
                  <div style={{ flexShrink: 0 }}>
                    <div style={{
                      height: '40px',
                      width: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#FFF7ED',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <NewspaperIcon style={{ height: '20px', width: '20px', color: '#EA580C' }} />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                      <h3 style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        color: COLORS.text.primary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        margin: 0
                      }}>
                        {newsletter.subject}
                      </h3>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: `${SPACING[4]} ${SPACING[8]}`,
                        borderRadius: RADIUS,
                        fontSize: TYPOGRAPHY.size.xs,
                        fontWeight: TYPOGRAPHY.weight.medium,
                        backgroundColor: '#FFF7ED',
                        color: '#EA580C'
                      }}>
                        Newsletter
                      </span>
                    </div>
                    <p style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.text.secondary,
                      marginTop: SPACING[4],
                      marginBottom: 0
                    }}>
                      {newsletter.person_detail?.name || newsletter.sender_name || newsletter.sender}
                    </p>
                    <p style={{
                      fontSize: TYPOGRAPHY.size.xs,
                      color: COLORS.text.tertiary,
                      marginTop: SPACING[4],
                      marginBottom: 0
                    }}>
                      {format(parseISO(newsletter.received_at), 'MMM d, yyyy • h:mm a')}
                    </p>
                  </div>
                </div>
                <button style={{
                  marginLeft: SPACING[16],
                  flexShrink: 0,
                  color: COLORS.text.tertiary,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0
                }}>
                  {expandedId === newsletter.id ? (
                    <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  ) : (
                    <svg style={{ height: '20px', width: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>
              </div>

              {!expandedId || expandedId !== newsletter.id ? (
                <div style={{
                  marginTop: SPACING[8],
                  fontSize: TYPOGRAPHY.size.sm,
                  color: COLORS.text.secondary,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {stripHtmlTags(newsletter.body).substring(0, 200)}...
                </div>
              ) : null}
            </div>

            {expandedId === newsletter.id && (
              <div style={{
                borderTop: `1px solid ${COLORS.border.default}`,
                backgroundColor: COLORS.bg.base,
                padding: SPACING[16],
                display: 'flex',
                flexDirection: 'column',
                gap: SPACING[16]
              }}>
                {/* AI Summary Section */}
                <div style={{
                  backgroundColor: '#FAF5FF',
                  border: '1px solid #E9D5FF',
                  borderRadius: RADIUS,
                  padding: SPACING[12]
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: SPACING[8]
                  }}>
                    <h4 style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: '#581C87',
                      display: 'flex',
                      alignItems: 'center',
                      margin: 0
                    }}>
                      <SparklesIcon style={{ height: '16px', width: '16px', marginRight: SPACING[4] }} />
                      AI Summary
                    </h4>
                    {!newsletter.summary && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateSummary(newsletter.id);
                        }}
                        disabled={summarizing[newsletter.id]}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: `${SPACING[4]} ${SPACING[8]}`,
                          fontSize: TYPOGRAPHY.size.xs,
                          fontWeight: TYPOGRAPHY.weight.medium,
                          borderRadius: RADIUS,
                          color: '#581C87',
                          backgroundColor: '#F3E8FF',
                          border: 'none',
                          cursor: summarizing[newsletter.id] ? 'not-allowed' : 'pointer',
                          opacity: summarizing[newsletter.id] ? 0.5 : 1
                        }}
                      >
                        <SparklesIcon style={{ height: '12px', width: '12px', marginRight: SPACING[4] }} />
                        {summarizing[newsletter.id] ? 'Generating...' : 'Generate Summary'}
                      </button>
                    )}
                  </div>
                  {newsletter.summary ? (
                    <div style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.text.primary,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {newsletter.summary}
                    </div>
                  ) : (
                    <p style={{
                      fontSize: TYPOGRAPHY.size.xs,
                      color: COLORS.text.secondary,
                      fontStyle: 'italic',
                      margin: 0
                    }}>
                      No summary yet. Click "Generate Summary" to create an AI-powered summary of this newsletter.
                    </p>
                  )}
                </div>

                {/* Full Content */}
                <div>
                  <h4 style={{
                    fontSize: TYPOGRAPHY.size.sm,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                    marginBottom: SPACING[8],
                    marginTop: 0
                  }}>Full Content</h4>
                  <div style={{ maxWidth: 'none' }}>
                    <div
                      style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        color: COLORS.text.primary
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(newsletter.body)
                      }}
                    />
                  </div>
                </div>

                <div style={{
                  marginTop: SPACING[16],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: SPACING[8],
                    fontSize: TYPOGRAPHY.size.xs,
                    color: COLORS.text.secondary
                  }}>
                    <EnvelopeOpenIcon style={{ height: '16px', width: '16px' }} />
                    <span>From: {newsletter.sender}</span>
                  </div>
                  <Link
                    to="/emails"
                    style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.accent.default,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      textDecoration: 'none'
                    }}
                  >
                    View in Emails →
                  </Link>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsletterList;
